import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileCheck2, Plus, Trash2, AlertTriangle, Building2, Hash } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

const STATUS = ["draft", "submitted", "approved", "inspection_scheduled", "passed", "failed", "closed"];
const LABEL = { draft: "Draft", submitted: "Submitted", approved: "Approved", inspection_scheduled: "Inspection set", passed: "Passed", failed: "Failed", closed: "Closed" };
const COLOR = {
  draft: "bg-slate-700/60 text-slate-200", submitted: "bg-blue-500/15 text-blue-300",
  approved: "bg-cyan-500/15 text-cyan-300", inspection_scheduled: "bg-amber-500/15 text-amber-300",
  passed: "bg-emerald-500/15 text-emerald-300", failed: "bg-rose-500/15 text-rose-300",
  closed: "bg-slate-700/60 text-slate-400",
};
const day = (s) => (s ? new Date(s).toLocaleDateString() : "");
const dtLocal = (s) => (s ? new Date(s).toISOString().slice(0, 10) : "");

export default function Permits() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", permit_type: "electrical", number: "", authority: "", status: "draft", inspection_at: "" });

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get(`/permits`, { params: filter ? { status: filter } : {} }); setData(data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); setData({ items: [], counts: {} }); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      const payload = { ...form, inspection_at: form.inspection_at ? new Date(form.inspection_at).toISOString() : null };
      await api.post(`/permits`, payload);
      toast.success("Permit added"); setShowNew(false);
      setForm({ title: "", permit_type: "electrical", number: "", authority: "", status: "draft", inspection_at: "" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api.patch(`/permits/${id}`, { status }); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setInspection = async (id, val) => {
    try { await api.patch(`/permits/${id}`, { inspection_at: val ? new Date(val).toISOString() : null }); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/permits/${id}`); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const counts = data?.counts || {};
  const inp = "rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none";

  return (
    <div className="space-y-6" data-testid="permits-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Eyebrow>Compliance</Eyebrow>
          <h1 className="mt-1 font-display text-2xl font-bold text-white">Permits & inspections</h1>
          <p className="mt-1 text-sm text-slate-400">Track every permit from submission through final inspection. Overdue inspections are flagged.</p>
        </div>
        <button data-testid="permit-new-btn" onClick={() => setShowNew((s) => !s)} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm"><Plus className="h-4 w-4" /> New permit</button>
      </div>

      {counts.overdue > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-rose-600/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="permit-overdue-banner">
          <AlertTriangle className="h-4 w-4" /> {counts.overdue} inspection{counts.overdue > 1 ? "s" : ""} overdue.
        </div>
      )}

      {showNew && (
        <div className="card-tac space-y-3 p-4" data-testid="permit-new-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="permit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Permit title (e.g. Service upgrade)" className={inp} />
            <select data-testid="permit-type" value={form.permit_type} onChange={(e) => setForm({ ...form, permit_type: e.target.value })} className={inp}>
              {["electrical", "plumbing", "mechanical", "building", "low_voltage", "general"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input data-testid="permit-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="Permit # (optional)" className={inp} />
            <input data-testid="permit-authority" value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} placeholder="Authority (e.g. City AHJ)" className={inp} />
            <select data-testid="permit-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inp}>
              {STATUS.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
            </select>
            <input data-testid="permit-inspection" type="date" value={form.inspection_at} onChange={(e) => setForm({ ...form, inspection_at: e.target.value })} className={inp} />
          </div>
          <button data-testid="permit-create" onClick={create} className="btn-amber rounded-full px-5 py-2 text-sm">Add permit</button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1 text-xs ${!filter ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`} data-testid="permit-filter-all">All</button>
        {STATUS.map((s) => (
          <button key={s} data-testid={`permit-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-xs ${filter === s ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`}>
            {LABEL[s]} {counts[s] ? `(${counts[s]})` : ""}
          </button>
        ))}
      </div>

      {data == null ? <div className="py-16"><Spinner label="Loading permits" /></div>
        : data.items.length === 0 ? <div className="card-tac py-16 text-center text-slate-500" data-testid="permits-empty">No permits yet. Add one to start tracking.</div>
          : (
            <div className="space-y-3">
              {data.items.map((p) => (
                <div key={p.id} data-testid={`permit-${p.id}`} className={`card-tac p-4 ${p.overdue ? "border-rose-600/40" : ""}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-white">{p.title}</span>
                        <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">{p.permit_type}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${COLOR[p.status] || "bg-slate-700 text-slate-200"}`}>{LABEL[p.status] || p.status}</span>
                        {p.overdue && <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] text-rose-300"><AlertTriangle className="h-3 w-3" /> Inspection overdue</span>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono-tac text-[10px] text-slate-500">
                        {p.number && <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{p.number}</span>}
                        {p.authority && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{p.authority}</span>}
                        {p.inspection_at && <span>Inspection: {day(p.inspection_at)}</span>}
                        {p.job_title && <Link to={`/jobs/${p.job_id}`} className="text-cyan-400 hover:underline">{p.job_title}</Link>}
                      </div>
                    </div>
                    <button data-testid={`permit-delete-${p.id}`} onClick={() => del(p.id)} className="rounded-full border border-slate-700 p-1.5 text-rose-300 hover:border-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select data-testid={`permit-status-${p.id}`} value={p.status} onChange={(e) => setStatus(p.id, e.target.value)} className="rounded-lg border border-slate-700 bg-[#0d1420] px-2 py-1 text-xs text-slate-200">
                      {STATUS.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
                    </select>
                    <label className="flex items-center gap-1 text-[11px] text-slate-500">Inspection
                      <input data-testid={`permit-inspection-${p.id}`} type="date" defaultValue={dtLocal(p.inspection_at)} onChange={(e) => setInspection(p.id, e.target.value)} className="rounded border border-slate-700 bg-[#0d1420] px-2 py-1 text-xs text-slate-200" />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
    </div>
  );
}
